﻿import {IProgress, isNullOrEmpty} from "@2d-front-monorepo/core";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {CreateOrSelectDriverModel} from "../components/CreateOrSelectDriverComponent";
import {CreateOrSelectResidencyModel} from "../components/CreateOrSelectResidencyComponent";
import {CreateOrSelectVehicleModel} from "../components/CreateOrSelectVehicleComponent";
import AttachmentType from "../enums/AttachmentType";
import IPermitsHttpClient from "../http/IPermitsHttpClient";
import ICreateComponentAttachments from "../interfaces/ICreateComponentAttachments";
import {CreatePermitArguments} from "../types/arguments/CreatePermitArguments";
import {FormDriverData, FormDriverId} from "../types/arguments/FormDriver";
import {FormHolder} from "../types/arguments/FormHolder";
import {FormResidencyData, FormResidencyId} from "../types/arguments/FormResidency";
import {FormVehicleData, FormVehicleId} from "../types/arguments/FormVehicle";
import {CreatePermitCommand, UpdateHolderModel} from "../types/CreatePermitCommand";
import {CreatePermitResult} from "../types/CreatePermitResult";
import {FormAttachment} from "../types/FormAttachment";
import {FormAttachmentData} from "../types/FormAttachmentData";
import {PhoneNumber} from "../types/PhoneNumber";

function countAttachments(attachments: Partial<Record<AttachmentType, FileList>>): number {
	let count = 0;
	for (const fileList of Object.values(attachments)) {
		if (fileList == null || fileList.length == 0) {
			continue;
		}

		if (fileList.length == 1) {
			count++;
		} else {
			count += fileList.length + 1;
		}
	}

	return count;
}

function countIAttachments(component: ICreateComponentAttachments): number {
	if (component.attachments == null) return 0;
	return countAttachments(component.attachments);
}

function countAllAttachments(state: CreatePermitCommand): number {
	return countIAttachments(state.permit)
		+ countIAttachments(state.driver)
		+ countIAttachments(state.residency)
		+ state.vehicles.reduce((previousValue, currentValue) => previousValue + countIAttachments(currentValue), 0);
}

function reportProgress(progress: IProgress, reporter?: ProgressReporter): void {
	if (reporter != null) reporter(progress);
}

function ensureProgress(attachments: Partial<Record<AttachmentType, FileList>>, label: string, progress?: IProgress): IProgress {
	progress ??= {current: 0, total: 0, label: ""};

	if (progress.total <= 0) {
		progress.total = countAttachments(attachments);
	}

	progress.label = label;
	return progress;
}

function normalizePhone(phone: PhoneNumber | undefined): PhoneNumber | undefined {
	if (phone == null || isNullOrEmpty(phone.phone)) {
		return undefined;
	}

	return phone;
}

export type AttachmentFormData = {
	data: FormData;
	type: AttachmentType;
}

export async function toFormData(httpClient: IPermitsHttpClient, attachments: Partial<Record<AttachmentType, FileList>> | undefined, progress?: IProgress, reporter?: ProgressReporter): Promise<AttachmentFormData[]> {
	if (attachments == null) return [];

	progress = ensureProgress(attachments, PermitTranslations.create_permit__upload_files, progress);
	reportProgress(progress, reporter);
	const result: AttachmentFormData[] = [];

	for (let [key, fileList] of Object.entries(attachments)) {
		let formData: FormData;
		if (fileList.length === 0) {
			continue;
		}

		if (fileList.length === 1) {
			formData = new FormData();
			formData.append("file", fileList[0]);

		} else {
			formData = new FormData();
			for (let i = 0, l = fileList.length; i < l; ++i) {
				formData.append("files", fileList[i]);
				++progress.current;
			}

			reportProgress(progress, reporter);
			const blob = await httpClient.files.mergeImages(formData);
			formData = new FormData();
			formData.append("file", blob);
		}

		result.push({
			data: formData,
			type: parseInt(key, 10) as AttachmentType
		});
	}

	return result;
}

export async function toFormAttachments(httpClient: IPermitsHttpClient, attachments: Partial<Record<AttachmentType, FileList>> | undefined, progress?: IProgress, reporter?: ProgressReporter): Promise<FormAttachment[]> {
	if (attachments == null) return [];

	progress = ensureProgress(attachments, PermitTranslations.create_permit__upload_files, progress);
	const formData = await toFormData(httpClient, attachments, progress, reporter);
	const result: FormAttachmentData[] = [];

	for (let {type, data} of formData) {
		++progress.current;
		reportProgress(progress, reporter);

		result.push({
			blobId: await httpClient.files.create(data),
			type: type
		});
	}

	return result;
}

async function toFormHolder(httpClient: IPermitsHttpClient, holder: UpdateHolderModel, progress: IProgress, reporter?: ProgressReporter | undefined): Promise<FormHolder | undefined> {
	const attachments = await toFormAttachments(httpClient, holder.attachments, progress, reporter);
	const holderArguments = holder.arguments;
	return {
		type: holderArguments.type,
		givenName: isNullOrEmpty(holderArguments.givenName) ? undefined : holderArguments.givenName,
		familyName: isNullOrEmpty(holderArguments.familyName) ? undefined : holderArguments.familyName,
		notificationsEmail: isNullOrEmpty(holderArguments.notificationsEmail) ? undefined : holderArguments.notificationsEmail,
		cellPhone: normalizePhone(holderArguments.cellPhone),
		homePhone: normalizePhone(holderArguments.homePhone),
		workPhone: normalizePhone(holderArguments.workPhone),
		company: holderArguments.company == null || (isNullOrEmpty(holderArguments.company.name) && isNullOrEmpty(holderArguments.company.taxId)) ? undefined : holderArguments.company,

		attachments: attachments,
		customData: holder.arguments.customData
	}
}

async function toFormDriver(httpClient: IPermitsHttpClient, driver: CreateOrSelectDriverModel, progress: IProgress, reporter?: ProgressReporter | undefined): Promise<FormDriverId | FormDriverData | undefined> {
	if (isNullOrEmpty(driver.driverId)) {
		return undefined;
	}

	const attachments = await toFormAttachments(httpClient, driver.attachments, progress, reporter);
	if (driver.driverId === "create") {
		return {
			fullName: driver.arguments.fullName,
			licenseNumber: driver.arguments.licenseNumber,
			attachments: attachments,
			customData: driver.arguments.customData
		}
	}

	return {
		id: driver.driverId,
		attachments: attachments,
		customData: driver.arguments.customData
	}
}

async function toFormResidency(httpClient: IPermitsHttpClient, residency: CreateOrSelectResidencyModel, progress: IProgress, reporter?: ProgressReporter | undefined): Promise<FormResidencyId | FormResidencyData | undefined> {
	if (isNullOrEmpty(residency.residencyId)) {
		return undefined;
	}

	const attachments = await toFormAttachments(httpClient, residency.attachments, progress, reporter);
	if (residency.residencyId === "create") {
		return {
			address: residency.arguments.address,
			uses: residency.arguments.uses,
			suffix: residency.arguments.suffix,
			attachments: attachments,
			customData: residency.arguments.customData
		}
	}

	return {
		id: residency.residencyId,
		attachments: attachments,
		customData: residency.arguments.customData
	}
}

async function toFormVehicle(httpClient: IPermitsHttpClient, vehicle: CreateOrSelectVehicleModel, progress: IProgress, reporter?: ProgressReporter | undefined): Promise<FormVehicleId | FormVehicleData> {
	const attachments = await toFormAttachments(httpClient, vehicle.attachments, progress, reporter);
	if (vehicle.vehicleId === "create") {
		return {
			plate: vehicle.arguments.plate,
			type: vehicle.arguments.type,
			data: vehicle.arguments.data,
			attachments: attachments,
			customData: vehicle.arguments.customData
		}
	}

	return {
		id: vehicle.vehicleId,
		attachments: attachments,
		customData: vehicle.arguments.customData
	}
}

export type ProgressReporter = (progress: IProgress) => void;

export async function createPermitFromCommand(httpClient: IPermitsHttpClient, state: CreatePermitCommand, reporter?: ProgressReporter | undefined): Promise<CreatePermitResult> {
	const progress: IProgress = {current: 0, total: countAllAttachments(state), label: ""};

	const args: CreatePermitArguments = {
		tariffId: state.tariffId,

		holder: await toFormHolder(httpClient, state.holder, progress, reporter),
		driver: await toFormDriver(httpClient, state.driver, progress, reporter),
		residency: await toFormResidency(httpClient, state.residency, progress, reporter),

		permit: {
			startDate: state.permit.arguments.startDate,
			endDate: state.permit.arguments.endDate,
			branch: state.permit.arguments.branch,
			billingData: state.permit.arguments.billingData,
			customData: state.permit.arguments.customData,
			vehicles: await Promise.all(state.vehicles.filter(x => !isNullOrEmpty(x.vehicleId)).map((vehicle: CreateOrSelectVehicleModel) => toFormVehicle(httpClient, vehicle, progress, reporter))),
			attachments: await toFormAttachments(httpClient, state.permit.attachments, progress, reporter),
			geolocation: undefined
		},

		customData: state.customData
	}

	progress.label = PermitTranslations.create_permit__create_permit;
	reportProgress(progress, reporter);
	return httpClient.permits.create(args);
}
